import AdUtils from '../../../../shared/utils/ads';
import ApiConfig from '@api';
import AppConfig from '@app';
import Background from '../../atoms/images/splashBackground';
import CmpUtils from '@shared/utils/cmp';
import Container from '../../atoms/container/splashContainer';
import Focus from '../../../../shared/types/enums/focus';
import Logo from '../../atoms/images/splashLogo';
import Package from '../../../../package.json';
import Pages from '@pages';
import Sections from '../../../../shared/types/enums/sections';
import Version from '../../atoms/text/splashVersion';
import useKeyboard from '../../../../shared/components/utils/hooks/useKeyboard';
import { appSelector } from '../../../../shared/store/slices/app.slice';
import { routerSelector } from '@shared/store/slices/router.slice';
import { setAds } from '../../../../shared/store/slices/ads.slice';
import { useAppDispatch, useAppSelector } from '../../../../shared/store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  filterFocus,
  focusSelector,
  popFocus,
  pushFocus,
  setFocus,
} from '../../../../shared/store/slices/focus.slice';

const OPACITY_TIMEOUT = 2 * 1000;
const SPLASH_TIMEOUT = 2 * 1000;

function Splash() {
  const dispatch = useAppDispatch();
  const {
    status: {
      splash: { ads, display },
      visibility,
    },
  } = useAppSelector(appSelector);
  const { focus } = useAppSelector(focusSelector);
  const { route } = useAppSelector(routerSelector);
  const [cmp, setCmp] = useState(false);
  const [focused, setFocused] = useState(display);
  const [resolved, setResolved] = useState(false);
  const [show, setShow] = useState(display);

  const render = show || !resolved;
  const focusRef = useRef(focus);
  const visibilityRef = useRef(visibility);
  focusRef.current = focus;

  const version = useMemo(() => {
    const version = `${Package.version}${
      (AppConfig.environment === 'dev' ||
        ApiConfig.environment === 'stage' ||
        window.location.href.includes('stage') ||
        window.location.href.includes('preproduction')) &&
      Package.rc
        ? `.${Package.rc}`
        : ''
    }`;
    return version;
  }, []);

  // Effect hooks

  useEffect(() => {
    if (visibility && !visibilityRef.current && render) {
      location.reload();
    }
    visibilityRef.current = visibility;
  }, [visibility]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined;
    let interval: ReturnType<typeof setInterval> | undefined = undefined;
    if (display) {
      focusRef.current !== Focus.SPLASH && dispatch(pushFocus(Focus.SPLASH));
    } else if (!resolved && !cmp) {
      timeout = setTimeout(() => {
        setFocused(() => {
          setTimeout(() => {
            setResolved(true);
            setShow(false);
          }, OPACITY_TIMEOUT);
          return false;
        });
        focusRef.current === Focus.SPLASH && dispatch(popFocus());
        dispatch(filterFocus(Focus.SPLASH));
        if (route.Page === Pages.LANGUAGES) return;
        if (ads) {
          const adSource = AdUtils.getSource(Sections.START_APP);
          dispatch(setAds(adSource));
        }
      }, SPLASH_TIMEOUT);
    }
    interval = setInterval(() => {
      setCmp(CmpUtils.isOpen());
    }, 1000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [ads, cmp, display, resolved]);

  useEffect(() => {
    if (!ads && !display && route.Page !== Pages.LANGUAGES) {
      dispatch(setFocus(Focus.MENU));
    }
  }, [ads, display]);

  // Keyboard hook

  useKeyboard({ focus: Focus.SPLASH, stopPropagation: true });

  return render ? (
    <Container
      animation={focused ? 'splash-fade-in' : 'splash-fade-out'}
      force={true}
    >
      <Background />
      <Version>{version}</Version>
      <Logo />
    </Container>
  ) : null;
}

export default Splash;
